import { useCallback, useRef, useState } from 'react';

/**
 * A tooltip on a button that opens a menu (the Tasks board's "Add view" "+",
 * "Board options" …, "Columns").
 *
 * Two ways such a tooltip came back at the wrong moment:
 *  - The menu is portaled, but React events bubble through portals along the
 *    COMPONENT tree, so moving the pointer onto the open menu reached the tooltip's
 *    child as a mouse-over and re-opened it beside the menu — and it then stayed up
 *    over the dialog a menu item launched. Closing it on pointer-down (72de83dac)
 *    was not enough for that reason.
 *  - When a menu closes it hands focus back to its trigger, which a tooltip reads as
 *    "focused, show me".
 *
 * So the owner reports when its menu — or a dialog the menu launched and the
 * trigger owns — is open, and while it is the tooltip is closed and refuses to
 * open. After it closes, the tooltip waits for a real pointer-enter on the trigger
 * before it may open again.
 */
export function useMenuTriggerTooltip() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    // Read through refs, not render closures: MUI opens after an enter delay with the
    // onOpen it captured on mouse-over, which predates the pointer-enter that re-armed it.
    const menuOpenRef = useRef(false);
    // False right after a menu closes: focus coming back, or the pointer resting where
    // the menu was, must not bring the tooltip back. A fresh pointer-enter re-arms it.
    const armedRef = useRef(true);

    const onTriggerPointerEnter = useCallback(() => { armedRef.current = true; }, []);
    const onTooltipOpen = useCallback(() => {
        if (!menuOpenRef.current && armedRef.current) setTooltipOpen(true);
    }, []);
    const onTooltipClose = useCallback(() => setTooltipOpen(false), []);
    const onMenuOpenChange = useCallback((open: boolean) => {
        if (open === menuOpenRef.current) return;
        menuOpenRef.current = open;
        setMenuOpen(open);
        setTooltipOpen(false);
        if (!open) armedRef.current = false;
    }, []);

    return { tooltipOpen: tooltipOpen && !menuOpen, onTooltipOpen, onTooltipClose, onMenuOpenChange, onTriggerPointerEnter };
}
